import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, FileText, Users, Music, Video, MailOpen, Calendar as CalIcon, Wrench, Folder, MessageSquare, LogOut, ShieldCheck, Presentation, ContactRound, Send, Sparkles } from "lucide-react";
import { PulseLogo } from "@/components/site/Header";
import VinylLoader from "@/components/site/VinylLoader";
import { useEffect } from "react";

const items = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Overview" },
  { to: "/admin/agent", icon: Sparkles, label: "Finance Agent" },
  { to: "/admin/contracts", icon: FileText, label: "Contracts" },
  { to: "/admin/services", icon: Wrench, label: "Services" },
  { to: "/admin/payments", icon: ShieldCheck, label: "Payments" },
  { to: "/admin/inquiries", icon: MessageSquare, label: "Inquiries" },
  { to: "/admin/artists", icon: Music, label: "Artists" },
  { to: "/admin/videos", icon: Video, label: "Videos" },
  { to: "/admin/team", icon: Users, label: "Team" },
  { to: "/admin/social", icon: MessageSquare, label: "Social Scheduler" },
  { to: "/admin/email", icon: MailOpen, label: "Email Campaigns" },
  { to: "/admin/deliverables", icon: Folder, label: "Deliverables" },
  { to: "/admin/calendar", icon: CalIcon, label: "Calendar" },
  { to: "/admin/pitch-deck", icon: Presentation, label: "Pitch Deck" },
  { to: "/admin/contacts", icon: ContactRound, label: "Contacts" },
  { to: "/admin/email", icon: Send, label: "Email Blasts" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  if (user === null) return <div className="pt-40 flex justify-center"><VinylLoader size={80} label="Authenticating" /></div>;
  if (user === false) return null;
  if (user && user.role !== "admin" && user.role !== "team") {
    return (
      <div className="pt-40 text-center" data-testid="admin-forbidden">
        <p className="text-zinc-400">Your account doesn't have admin access.</p>
        <button onClick={() => { logout(); navigate("/login"); }} className="glass-btn ghost mt-6">Sign out</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-[260px] flex-col border-r border-white/5 bg-[#08080A] sticky top-0 h-screen">
        <div className="p-6 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <PulseLogo size={32} />
            <div>
              <div className="font-display font-bold tracking-tight text-sm">PULSE MEDIA</div>
              <div className="label-mono" style={{ fontSize: 9 }}>/ ADMIN</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto space-y-1">
          {items.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g,'-')}`}
            >
              <Icon size={16} strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-sm truncate">{user.name}</div>
              <div className="label-mono">{user.role}</div>
            </div>
            <button onClick={async () => { await logout(); navigate("/"); }} className="glass-btn ghost p-2" data-testid="admin-logout">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-white/5" style={{ background: "rgba(8,8,10,0.85)" }}>
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2"><PulseLogo size={24}/><div className="font-mono text-xs">PULSE / ADMIN</div></Link>
          <button onClick={async () => { await logout(); navigate("/"); }} className="glass-btn ghost p-2"><LogOut size={14}/></button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-4 pb-3 no-scrollbar">
          {items.map(({ to, label, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex-none px-3 py-1.5 rounded-full text-xs whitespace-nowrap border ${isActive ? "bg-cyan-500/10 border-cyan-400/40 text-cyan-300" : "border-white/10 text-zinc-400"}`}>
              {label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="flex-1 lg:py-10 lg:px-10 pt-32 px-4 pb-20" data-testid="admin-content">
        <Outlet />
      </main>
    </div>
  );
}
